/**
 * Which estimate this browser profile was last working on.
 *
 * Why this exists: the app's session lives in localStorage, which is shared by
 * every tab of a browser PROFILE, not per tab and not per person. On a shared
 * estimating machine a fresh tab therefore comes up inside whoever's session
 * was last opened, with their job as the save target — and nothing on screen
 * says so. A twelve-job replay hit exactly this and lost track of which
 * estimate it was editing.
 *
 * The marker is deliberately tiny: an id, a name, who left it and when. It is
 * NEVER estimate state — no line items, no prices, nothing that could come
 * back stale and be mistaken for a priced job. Its only job is to let the app
 * ASK before continuing someone else's work.
 *
 * Storage is best-effort: private mode, sandboxed frames and disabled storage
 * all degrade to "no marker", which simply means the app starts fresh.
 */

export const SESSION_HANDOFF_KEY = "costpilot.session.activeEstimate";

/**
 * The signed-in person's email, from whatever shape auth handed us.
 *
 * AuthContext publishes `currentUser`; the JWT login, the MSAL exchange and the
 * cached-profile refresh all build that object separately, so the address can
 * arrive as `email`, `mail` or `userPrincipalName`. A marker written without an
 * email is a resume prompt that cannot say whose estimate it is offering, which
 * is the entire reason the prompt exists — so read all of them, and fall back
 * to the cached profile before giving up.
 */
export function resolveUserEmail(user) {
  const fromUser = pickEmail(user);
  if (fromUser) return fromUser;
  return pickEmail(readCachedUser());
}

function pickEmail(candidate) {
  if (!candidate || typeof candidate !== "object") return null;
  for (const key of ["email", "mail", "userPrincipalName", "username"]) {
    const value = candidate[key];
    // `username` is a last resort and only counts when it IS an address.
    if (typeof value === "string" && value.includes("@") && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

function readCachedUser() {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function storage() {
  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
}

/** Remember the estimate this session is on. Nulls/blank ids clear it. */
export function rememberActiveEstimate({ jobId, jobName, userEmail } = {}) {
  const store = storage();
  if (!store) return;
  if (jobId === null || jobId === undefined || jobId === "") {
    forgetActiveEstimate();
    return;
  }
  try {
    store.setItem(
      SESSION_HANDOFF_KEY,
      JSON.stringify({
        jobId,
        jobName: jobName || "Untitled Estimate",
        userEmail: userEmail || null,
        at: new Date().toISOString(),
      }),
    );
  } catch (error) {
    /* storage unavailable — the app just won't offer a resume */
  }
}

/** Drop the marker (new job, sign-out, "start fresh"). */
export function forgetActiveEstimate() {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(SESSION_HANDOFF_KEY);
  } catch (error) {
    /* nothing to do */
  }
}

/**
 * The marker, or null. Anything malformed reads as null and is removed:
 * a resume prompt that cannot describe what it would resume is worse than none.
 */
export function readActiveEstimate() {
  const store = storage();
  if (!store) return null;
  let raw = null;
  try {
    raw = store.getItem(SESSION_HANDOFF_KEY);
  } catch (error) {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") throw new Error("not an object");
    if (parsed.jobId === null || parsed.jobId === undefined)
      throw new Error("no job id");
    return {
      jobId: parsed.jobId,
      jobName:
        typeof parsed.jobName === "string" && parsed.jobName.trim()
          ? parsed.jobName
          : "Untitled Estimate",
      userEmail: typeof parsed.userEmail === "string" ? parsed.userEmail : null,
      at: typeof parsed.at === "string" ? parsed.at : null,
    };
  } catch (error) {
    forgetActiveEstimate();
    return null;
  }
}

/**
 * True when the marker was left by someone other than whoever is signed in.
 * Comparison is case-insensitive: email case is not identity.
 */
export function isDifferentOwner(marker, currentEmail = null) {
  const owner = marker?.userEmail;
  if (!owner || !currentEmail) return false;
  return (
    owner.trim().toLowerCase() !== String(currentEmail).trim().toLowerCase()
  );
}

/**
 * Whose session this is, said plainly.
 *
 * `currentEmail` is who the browser is signed in as now. When the marker was
 * left by someone else that is the headline of the prompt, because continuing
 * would save into their estimate under their login.
 */
export function describeHandoff(marker, currentEmail = null) {
  if (!marker) return "";
  const owner = marker.userEmail;
  if (isDifferentOwner(marker, currentEmail)) {
    return `Left open by ${owner} — this browser is signed in as ${currentEmail}.`;
  }
  if (owner) return `Left open by ${owner} on this computer.`;
  return "Left open on this computer.";
}
